const categoryContent = {
  title: '카테고리 관리 매뉴얼',
  content: `
  <div class="page-title">[카테고리 관리]</div>
  
# [개요]
카테고리 관리 기능은 웹사이트의 비즈니스를 체계적으로 분류하고 구조화하기 위한 도구입니다. 이 문서에서는 카테고리의 등록, 수정, 순서 변경 등 전반적인 관리 방법을 확인할 수 있습니다.

### 📋 주요 기능 요약
| 기능 | 설명 | 권한 |
|------|------------------|------------|
| 카테고리 조회 | 트리 구조로 카테고리 목록 확인 | 관리자 |
| 카테고리 등록 | 신규 카테고리 생성 및 설정 | 관리자 |
| 카테고리 수정 | 기존 카테고리 정보 수정 | 관리자 |
| 순서 관리 | 카테고리 순서 변경 | 관리자 |

# [조회]
등록된 카테고리를 트리 구조로 조회하고 관리할 수 있습니다.

### 📋 화면 구성 및 기능
| 영역 | 구성 | 주요 기능 |
|------|------|-----------|
| 검색 영역 | 검색 조건 | • 검색<br>•  활성화 상태 설정 |
| 트리 영역 | 카테고리 트리 | • 계층 구조 확인<br>• 확장/축소 기능<br>• 하위 카테고리 개수 표시 |
| 버튼 영역 | 관리 버튼 | • 신규 등록<br>• 순서 변경 |

![카테고리 목록](/assets/images/guide/category/list.png)

# [순서변경]
그리드 좌측 상단의 '순서 변경' 버튼 선택 시 카테고리의 순서를 관리할 수 있습니다.

### 📋 화면 구성 및 기능
| 영역 | 구성 | 주요 기능 |
|------|------|-----------|
| 순서 관리 | 드래그 앤 드롭 영역 | • 카테고리 순서 변경<br>• 실시간 순서 저장 |
| 카테고리 목록 | 카테고리 항목 | • 카테고리만 표시<br>• 비즈니스 제외 |
| 사용 여부 | 사용 여부 필터 | • 사용 여부 검색 |

> 💡 **Tip**
> - 드래그 앤 드롭으로 순서를 변경할 수 있습니다.
> - 순서 변경 시 실시간으로 저장됩니다.
> - 비즈니스 항목은 표시되지 않습니다. 
> - 카테고리와 비즈니스의 통합 순서 관리는 비즈니스 관리 메뉴에서 진행할 수 있습니다.


![카테고리 순서관리](/assets/images/guide/category/order.png)

# [등록/수정]
다국어 지원 카테고리 콘텐츠를 신규 등록하거나 기존 정보를 수정할 수 있습니다.

### 📋 화면 구성 및 기능
| 영역 | 입력 항목 | 설명 |
|------|-----------|------|
| 기본 설정 | • 카테고리 타입<br>• 상위 카테고리<br>• 사용 여부 | • 계층 구조 설정<br>• 비즈니스 레이아웃 결정<br>• 활성화 상태 설정 |
| 이미지 설정 | • 썸네일<br>• 배경 이미지(데스크탑용)<br>• 배경 이미지(모바일용) | • 메인 화면 노출 이미지<br>• PC 상세페이지 배경<br>• 모바일 상세페이지 배경 |
| 다국어 설정 | • 카테고리명<br>• 설명 | • 언어별 제목 입력<br>• 언어별 설명 입력<br>• 모든 언어 동시 입력 가능 |

> 💡 **Tip**
> - 모든 언어의 카테고리명을 한 번에 입력하고 저장할 수 있습니다.
> - 배경 이미지는 각 카테고리 상세 페이지의 배경으로 사용됩니다.

> ⚠️ **주의사항**
> - 이미지는 데스크탑용과 모바일용을 각각 업로드해야 합니다.
> - 카테고리 타입 설정 시 하위 비즈니스의 레이아웃이 결정됩니다.
> - 필수 언어(한국어/영어)는 반드시 입력해야 합니다.

![카테고리 등록](/assets/images/guide/category/edit.png)

### 🎨 타입별 스타일 가이드
카테고리의 타입 설정에 따라 비즈니스의 레이아웃이 결정됩니다.

| 카테고리 타입 | 특징 | 카테고리 레이아웃 | 입력된 항목만 노출 |
|------------|------|-----------------|-----------|
| TYPE A | • 탭 형태로 노출<br>• 비즈니스 간 전환 가능 | • 최상위 카테고리와 하위 카테고리가 탭으로 노출<br>• 카테고리에 파이프라인 리스트 추가<br>(비즈니스 기준으로 그래프 표시)<br>• 파이프라인이 없는 경우 해당 영역 미노출 | • 최상위 카테고리:<br>- main title<br>- category name<br>• 하위 카테고리:<br>- main title<br>- sub title<br>- main descript<br>- sub descript<br>- 이미지 |
| TYPE B | • 리스트 형태로 노출<br>• 순차적 스크롤 | • 아래 항목들이 순차적으로 노출:<br>- main title<br>- summary<br>- category descript | • main title<br>• summary<br>• category descript<br>• 이미지<br> |

> 💡 **Tip**
> - 카테고리 타입은 하위 비즈니스의 레이아웃을 결정합니다.
> - 비즈니스의 특성에 맞는 타입을 선택하세요.
> - 카테고리 타입 변경 시 하위 비즈니스의 레이아웃도 함께 변경됩니다.
> - 파이프라인 설정은 카테고리 단위로 관리됩니다.

<div class="image-row">
  <div class="image-col">
    <img src="/assets/images/guide/business/typeA.jpeg" alt="TYPE A 카테고리와 비즈니스" />
  </div>
  <div class="image-col">
    <img src="/assets/images/guide/business/typeB.jpeg" alt="TYPE B 카테고리와 비즈니스" />
  </div>
</div>

*TYPE A 탭 형태(좌)와 TYPE B 리스트 형태(우) 레이아웃 비교*

`,
};
export default categoryContent;
